import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { FlexBox } from '../box';
import { IconButton } from '../button';
import { styled } from '../styled';
import { Tooltip } from '../tooltip';
import { Typography, TypographyWithTooltip } from '../typography';
import { withVersion } from '../utils';
const ClickableTypography = styled(Typography)(() => ({
    cursor: 'pointer'
}));
const ClickableTypographyWithTooltip = styled(TypographyWithTooltip)(() => ({
    cursor: 'pointer'
}));
export function maskEmail(email) {
    const atIndex = email.indexOf('@');
    if (atIndex === -1) {
        return email;
    }
    const local = email.slice(0, atIndex);
    const domain = email.slice(atIndex + 1);
    if (local.length === 0) {
        return email;
    }
    if (local.length === 1) {
        return `*@${domain}`;
    }
    if (local.length === 2) {
        return `**@${domain}`;
    }
    return `${local[0]}${'*'.repeat(local.length - 2)}${local[local.length - 1]}@${domain}`;
}
const MaskedEmailInner = ({ email, canReveal = false, showTooltip = 'Show email', hideTooltip = 'Hide email', hiddenTooltip = 'Email address hidden', onEmailClick, copyEnabled = false, copyTooltip = 'Copy email', copiedTooltip = 'Copied', legacyStyle, className, 'data-role': dataRole = 'masked-email', 'data-role-value': dataRoleValue }) => {
    const [revealed, setRevealed] = React.useState(false);
    const [copied, setCopied] = React.useState(false);
    const copiedTimerRef = React.useRef(null);
    const isRevealed = canReveal && revealed;
    const displayText = isRevealed ? email : maskEmail(email);
    const colorOverride = legacyStyle?.color
        ? () => legacyStyle.color
        : undefined;
    const iconColorProps = colorOverride ? { color: colorOverride } : undefined;
    React.useEffect(() => {
        return () => {
            if (copiedTimerRef.current !== null) {
                clearTimeout(copiedTimerRef.current);
            }
        };
    }, []);
    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(email);
            if (copiedTimerRef.current !== null) {
                clearTimeout(copiedTimerRef.current);
            }
            setCopied(true);
            copiedTimerRef.current = setTimeout(() => {
                setCopied(false);
                copiedTimerRef.current = null;
            }, 1500);
        }
        catch {
            // clipboard unavailable or permission denied — no confirmation shown
        }
    };
    if (!canReveal) {
        const TextComponent = onEmailClick ? ClickableTypography : Typography;
        return (_jsx(Tooltip, { title: hiddenTooltip, placement: "top", children: _jsx(TextComponent, { component: "span", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, maxWidth: "100%", display: "block", role: onEmailClick ? 'button' : undefined, tabIndex: onEmailClick ? 0 : undefined, onClick: onEmailClick, onKeyDown: onEmailClick
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onEmailClick();
                        }
                    }
                    : undefined, className: className, "data-role": dataRole, "data-role-value": dataRoleValue, children: displayText }) }));
    }
    const RevealTextComponent = onEmailClick
        ? ClickableTypographyWithTooltip
        : TypographyWithTooltip;
    return (_jsxs(FlexBox, { alignItems: "center", gap: 0.25, display: "flex", minWidth: 0, maxWidth: "100%", className: className, "data-role": dataRole, "data-role-value": dataRoleValue, onClick: (e) => e.stopPropagation(), children: [copyEnabled && (_jsx(FlexBox, { flexShrink: 0, children: _jsx(Tooltip, { title: copied ? copiedTooltip : copyTooltip, placement: "top", open: copied ? true : undefined, disableFocusListener: copied, disableHoverListener: copied, disableTouchListener: copied, children: _jsx("span", { children: _jsx(IconButton, { icon: "IconContentCopy", size: "small", disabled: copied, iconProps: iconColorProps, onClick: (e) => {
                                e.stopPropagation();
                                void handleCopy();
                            }, "aria-label": copyTooltip, "data-role": "masked-email-copy" }) }) }) })), _jsx(RevealTextComponent, { component: "span", role: onEmailClick ? 'button' : undefined, tabIndex: onEmailClick ? 0 : undefined, onClick: onEmailClick, onKeyDown: onEmailClick
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onEmailClick();
                        }
                    }
                    : undefined, flex: 1, minWidth: 0, 
                // eslint-disable-next-line react/forbid-component-props
                style: legacyStyle, children: displayText }), _jsx(FlexBox, { flexShrink: 0, children: _jsx(Tooltip, { title: isRevealed ? hideTooltip : showTooltip, placement: "top", children: _jsx(IconButton, { icon: isRevealed ? 'IconVisibilityOff' : 'IconVisibility', size: "small", iconProps: iconColorProps, onClick: (e) => {
                            e.stopPropagation();
                            setRevealed((v) => !v);
                        }, "aria-label": isRevealed ? hideTooltip : showTooltip, "aria-pressed": isRevealed, "data-role": "masked-email-toggle" }) }) })] }));
};
const MaskedEmailImpl = (props) => (_jsx(MaskedEmailInner, { ...props }, props.email));
MaskedEmailImpl.displayName = 'MaskedEmail';
export const MaskedEmail = withVersion(MaskedEmailImpl, '1.1.0');
